import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { safeStorageJson } from '@/lib/safeJson';

interface VideoMeetingNotification {
  id: string;
  title: string;
  scheduled_for: string;
  meeting_url: string | null;
  deal_id: string | null;
  contact_id: string | null;
  lead_id: string | null;
}

export function useVideoMeetingNotifications() {
  const [meetings, setMeetings] = useState<VideoMeetingNotification[]>([]);
  const [readIds, setReadIds] = useState<Set<string>>(
    () => new Set(safeStorageJson<string[]>('video_meeting_read_ids', []))
  );
  const [appUserId, setAppUserId] = useState<string | null>(null);
  const { user } = useAuth();

  // Resolve auth user to app_user_id
  useEffect(() => {
    if (!user?.id) { setAppUserId(null); return; }
    supabase
      .from('app_users')
      .select('id')
      .eq('auth_user_id', user.id)
      .maybeSingle()
      .then(({ data }) => setAppUserId(data?.id ?? null));
  }, [user?.id]);

  const loadMeetings = useCallback(async () => {
    if (!appUserId) return;
    const { data, error } = await supabase
      .from('calls')
      .select('id, title, scheduled_for, meeting_url, deal_id, contact_id, lead_id')
      .eq('channel', 'video')
      .eq('status', 'scheduled')
      .or(`created_by.eq.${appUserId},handled_by_app_user_id.eq.${appUserId}`)
      .gte('scheduled_for', new Date().toISOString())
      .order('scheduled_for', { ascending: true });

    if (error) {
      console.error('Error loading video meeting notifications:', error);
      return;
    }
    setMeetings((data || []) as VideoMeetingNotification[]);
  }, [appUserId]);

  useEffect(() => {
    if (!appUserId) return;
    loadMeetings();

    const interval = setInterval(loadMeetings, 60000);

    const channel = supabase
      .channel('video-meeting-bell-notifications')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'calls',
        },
        (payload) => {
          if ((payload.new as any)?.channel === 'video') {
            loadMeetings();
          }
        }
      )
      .subscribe();

    return () => {
      clearInterval(interval);
      supabase.removeChannel(channel);
    };
  }, [appUserId, loadMeetings]);

  const unreadCount = meetings.filter(m => !readIds.has(m.id)).length;

  const markAsRead = useCallback((meetingId: string) => {
    setReadIds(prev => {
      const next = new Set(prev);
      next.add(meetingId);
      localStorage.setItem('video_meeting_read_ids', JSON.stringify(Array.from(next)));
      return next;
    });
  }, []);

  return { meetings, unreadCount, markAsRead };
}
